import { useLabels } from "@/core/context/LabelsContext"
import { ProductMemberMultiSelectQuery } from "@/product/member/common/__generated__/ProductMemberMultiSelectQuery.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import UserDropdownItem from "@/user/common/UserDropdownItem"
import { DiscoInputSkeleton } from "@disco-ui"
import DiscoMultiSelect, {
  DiscoMultiSelectOption,
} from "@disco-ui/select/DiscoMultiSelect"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { AutocompleteGetTagProps } from "@material-ui/lab"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, useLazyLoadQuery } from "react-relay"

export type MemberMultiSelect = { id: GlobalID; memberId: GlobalID }

interface Props extends TestIDProps {
  placeholder?: string
  productId: GlobalID
  values: GlobalID[]
  onChange: (values: MemberMultiSelect[]) => void
  hideUserIds?: GlobalID[]
}

function ProductMemberMultiSelect(props: Props) {
  const {
    testid = "ProductMemberMultiSelect",
    placeholder,
    productId,
    values,
    onChange,
  } = props

  const { product } = useLazyLoadQuery<ProductMemberMultiSelectQuery>(
    graphql`
      query ProductMemberMultiSelectQuery($id: ID!) {
        product: node(id: $id) {
          ... on Product {
            productMemberships {
              edges {
                node {
                  id
                  email
                  ...UserDropdownItemPrivateFragment
                  member {
                    id
                    fullName
                    ...UserDropdownItemFragment
                  }
                }
              }
            }
          }
        }
      }
    `,
    { id: productId }
  )

  const memberships = Relay.connectionToArray(product?.productMemberships)
  const membershipsById = ArrayUtils.mapBy(memberships, "id")
  const hideUserIds = new Set(props.hideUserIds)
  const visibleMemberships =
    hideUserIds.size > 0
      ? memberships.filter((m) => !hideUserIds.has(m.member.id))
      : memberships

  const labels = useLabels()

  return (
    <DiscoMultiSelect
      testid={testid}
      placeholder={placeholder || labels.product_member.plural}
      values={values}
      onChange={handleChange}
      renderOption={renderOption}
      renderTags={renderTags}
      options={visibleMemberships.map((pm) => ({
        key: pm.id,
        value: pm.id,
        title: pm.member.fullName,
        searchable: [pm.member.fullName, pm.email],
      }))}
      additionalSubmitKeys={[","]}
      filterOptions={{ maxVisible: 100 }}
    />
  )

  // Add memberId to the change for ease of finding userId for each membership..
  function handleChange(productMembershipIds: string[]) {
    onChange(
      productMembershipIds
        .filter((id) => membershipsById[id])
        .map((id) => ({
          id: membershipsById[id].id,
          memberId: membershipsById[id].member.id,
        }))
    )
  }

  function renderTags(
    value: DiscoMultiSelectOption[],
    getTagProps: AutocompleteGetTagProps
  ) {
    return value.map((pm, index) => (
      <DiscoTag
        key={pm.value}
        testid={`${testid}.option.remove.${pm.title}`}
        {...getTagProps({ index })}
        name={pm.title}
      />
    ))
  }

  function renderOption(option: DiscoMultiSelectOption) {
    const membership = membershipsById[option.value]
    if (!membership) return <></>
    return (
      <UserDropdownItem
        testid={`${testid}.option.${option.title}`}
        userKey={membership.member}
        privateUserKey={membership}
      />
    )
  }
}

export default Relay.withSkeleton<Props>({
  component: ProductMemberMultiSelect,
  skeleton: () => <DiscoInputSkeleton />,
})
